import React, { useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { toast } from "react-hot-toast";
import API from "../api";
import { capture } from "../instrument";

const MutuelleCodeLookup = ({ onChange, initialValues = [], placeholder = "Rechercher une mutuelle par code" }) => {
  const [inputValue, setInputValue] = useState("");
  const [tags, setTags] = useState([]);
  const [mutuelleSuggestions, setMutuelleSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const suggestionItemsRef = useRef([]);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!Array.isArray(initialValues)) return;
    setTags([...new Set(initialValues.filter((value) => typeof value === "string" && value.trim() !== ""))]);
  }, [initialValues]);

  useEffect(() => {
    suggestionItemsRef.current = suggestionItemsRef.current.slice(0, mutuelleSuggestions.length);
  }, [mutuelleSuggestions]);

  useEffect(() => {
    if (selectedIndex >= 0 && suggestionItemsRef.current[selectedIndex]) {
      suggestionItemsRef.current[selectedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (inputValue?.length < 2) {
      setMutuelleSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(() => fetchMutuelleSuggestions(inputValue), 200);
    return () => clearTimeout(timer);
  }, [inputValue, tags]);

  const fetchMutuelleSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setMutuelleSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      setIsLoading(true);
      const encodedQuery = encodeURIComponent(query);
      const response = await API.get(`/mutuelle?q=${encodedQuery}`, { signal });
      if (signal.aborted) return;
      if (!response.ok) return toast.error(response.message || "Erreur lors de la recherche");

      const suggestions = (response.data || [])
        .filter((mutuelle) => typeof mutuelle?.code === "string" && mutuelle.code.trim() !== "")
        .filter((mutuelle) => !tags.includes(mutuelle.code));

      setMutuelleSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
      setSelectedIndex(suggestions.length > 0 ? 0 : -1);
    } catch (error) {
      if (error.name === "AbortError") return;
      capture(error);
      console.log(error);
      toast.error("Une erreur est survenue lors de la recherche de mutuelle");
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  };

  const updateTags = (nextTags) => {
    setTags(nextTags);
    if (onChange) onChange(nextTags);
  };

  const addTag = (codeValue) => {
    const code = (codeValue || "").trim();
    if (!code) return;
    if (tags.includes(code)) return toast.error("Le code mutuelle est déjà présent");

    const nextTags = [...tags, code];
    updateTags(nextTags);

    setInputValue("");
    setMutuelleSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    if (inputRef.current) inputRef.current.focus();
  };

  const validateAndAddTag = async (rawInput) => {
    if (!rawInput) return;

    setInputValue("");
    setMutuelleSuggestions([]);
    setShowSuggestions(false);
    if (abortControllerRef.current) abortControllerRef.current.abort();

    try {
      setIsLoading(true);
      const encodedQuery = encodeURIComponent(rawInput);
      const response = await API.get(`/mutuelle?q=${encodedQuery}`);

      if (!response.ok) return addTag(rawInput);

      const exactMatch = (response.data || []).find((m) => m.code?.toUpperCase() === rawInput.toUpperCase());
      addTag(exactMatch ? exactMatch.code : rawInput);
    } catch (error) {
      capture(error);
      addTag(rawInput);
    } finally {
      setIsLoading(false);
    }
  };

  const removeTag = (tagToRemove) => {
    const nextTags = tags.filter((tag) => tag !== tagToRemove);
    updateTags(nextTags);
    if (inputRef.current) inputRef.current.focus();
  };

  const suggestionsOpen = showSuggestions && mutuelleSuggestions.length > 0;

  const navOpts = { enabled: suggestionsOpen, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys("down", () => setSelectedIndex((i) => Math.min(i + 1, mutuelleSuggestions.length - 1)), navOpts, [suggestionsOpen, mutuelleSuggestions]);
  useHotkeys("up", () => setSelectedIndex((i) => Math.max(i - 1, 0)), navOpts, [suggestionsOpen, mutuelleSuggestions]);
  useHotkeys(
    "enter",
    () => {
      const selectedSuggestion = mutuelleSuggestions[selectedIndex >= 0 ? selectedIndex : 0];
      if (selectedSuggestion?.code) addTag(selectedSuggestion.code);
    },
    navOpts,
    [suggestionsOpen, mutuelleSuggestions, selectedIndex],
  );
  useHotkeys("escape", () => { setShowSuggestions(false); setSelectedIndex(-1); }, navOpts, [suggestionsOpen, mutuelleSuggestions]);

  const onKeyDown = (e) => {
    if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
      return;
    }
    if (e.key === "Enter" && inputValue.trim() !== "" && !suggestionsOpen) {
      e.preventDefault();
      validateAndAddTag(inputValue.trim());
    }
  };

  return (
    <div className="relative w-full">
      <div className="bg-white flex flex-wrap items-center gap-1 p-2 border rounded-lg transition-all duration-200 min-h-[42px] cursor-text" onClick={() => inputRef.current?.focus()}>
        {tags.map((tag, idx) => (
          <div key={`${tag}-${idx}`} className="bg-primary-100 text-primary-800 px-1 py-0.5 rounded-md text-sm flex items-center">
            {tag}
            <button
              className="ml-1 text-primary-600 hover:text-primary-800"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(tag);
              }}
              type="button"
              aria-label={`Supprimer ${tag}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        ))}

        <div className="flex items-center flex-grow">
          <input
            ref={inputRef}
            className="flex-grow outline-none min-w-[120px]"
            placeholder={tags.length === 0 ? placeholder : ""}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={() =>
              setTimeout(() => {
                setShowSuggestions(false);
                setSelectedIndex(-1);
              }, 200)
            }
            onFocus={() => {
              if (inputValue.length >= 2) fetchMutuelleSuggestions(inputValue);
            }}
          />
          {isLoading && <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full mr-2"></div>}
        </div>
      </div>

      {showSuggestions && mutuelleSuggestions.length > 0 && (
        <div ref={suggestionsRef} className="absolute z-10 mt-1 max-h-60 w-full overflow-auto dropdown-panel">
          {mutuelleSuggestions.map((suggestion, idx) => (
            <div
              ref={(el) => (suggestionItemsRef.current[idx] = el)}
              key={suggestion._id || `${suggestion.code}-${idx}`}
              className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-2 transition-colors ${
                selectedIndex === idx ? "bg-primary-50" : "hover:bg-gray-50"
              }`}
              onClick={() => addTag(suggestion.code)}
            >
              <div className="flex min-w-0 items-baseline gap-2">
                <span className={`shrink-0 text-sm font-semibold ${selectedIndex === idx ? "text-primary-700" : "text-gray-900"}`}>{suggestion.code}</span>
                {suggestion.name && <span className="truncate text-xs text-gray-600">{suggestion.name}</span>}
              </div>
              {selectedIndex === idx && (
                <span className="shrink-0 whitespace-nowrap rounded border border-primary-200 bg-primary-50 px-1.5 py-0.5 text-[10px] font-medium text-primary-600">Entrée ↵</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MutuelleCodeLookup;
